import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import MentionComponent from "./MentionComponent";
import EntryComponent from "./EntryComponent";
import "./SimplePostEditor.scss";

const SimplePostEditor = ({ 
  className, 
  style, 
  placeholder, 
  mentions = [], 
  getValue, 
  onChange, 
  initialValue, 
  editorRef 
}) => {
  const editorRefInternal = useRef(null);
  const suggestionsRef = useRef(null);
  
  const [content, setContent] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(null);
  const [cursorTop, setCursorTop] = useState(0);
  const [cursorLeft, setCursorLeft] = useState(0);

  const convertRawToHTML = useCallback((rawData) => {
    // Convert raw format to HTML (matching PostEditor behavior)
    if (!rawData || !rawData.blocks) return "";
    
    return rawData.blocks
      .map((block) => {
        let text = block.text;
        let html = text;
        
        // Apply inline styles first (bold, italic, underline)
        if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
          const sortedStyles = [...block.inlineStyleRanges].sort(
            (a, b) => b.offset - a.offset
          );
          
          sortedStyles.forEach((styleRange) => {
            const start = styleRange.offset;
            const end = start + styleRange.length;
            const before = html.substring(0, start);
            const styledText = html.substring(start, end);
            const after = html.substring(end);
            
            let tag = "";
            if (styleRange.style === "BOLD") tag = "strong";
            else if (styleRange.style === "ITALIC") tag = "em";
            else if (styleRange.style === "UNDERLINE") tag = "u";
            
            if (tag) {
              html = `${before}<${tag}>${styledText}</${tag}>${after}`;
            }
          });
        }
        
        // Apply entity ranges (mentions) - process in reverse order
        if (block.entityRanges && block.entityRanges.length > 0) {
          const sortedRanges = [...block.entityRanges].sort(
            (a, b) => b.offset - a.offset
          );
          
          sortedRanges.forEach((entityRange) => {
            const entity = rawData.entityMap[entityRange.key];
            if (entity && (entity.type === "MENTION" || entity.type === "mention")) {
              const mention = entity.data.mention;
              const start = entityRange.offset;
              const end = start + entityRange.length;
              const before = html.substring(0, start);
              // Remove @ if present, but mentions shouldn't have @
              let mentionText = html.substring(start, end);
              // Remove @ symbol if present
              if (mentionText.startsWith("@")) {
                mentionText = mentionText.substring(1);
              }
              const after = html.substring(end);
              html = `${before}<span class="mention_class position-relative" data-mention-id="${mention.OwnerUserName}" data-role-id="${mention.name || mentionText}">${mentionText}</span>${after}`;
            }
          });
        }
        
        return html;
      })
      .join("<br>");
  }, []);

  const getRawState = useCallback(() => {
    if (!editorRefInternal.current) return null;

    const content = editorRefInternal.current.innerHTML;
    const text = editorRefInternal.current.innerText || editorRefInternal.current.textContent;

    // Extract mentions and inline styles from HTML
    const mentionsList = [];
    const inlineStyleRanges = [];
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;

    // Match both .mention and .mention_class for compatibility
    const mentionElements = tempDiv.querySelectorAll(".mention, .mention_class");
    mentionElements.forEach((el) => {
      const mentionId = el.getAttribute("data-mention-id");
      let mentionText = el.textContent;
      // Remove @ symbol if present (mentions shouldn't have @)
      if (mentionText.startsWith("@")) {
        mentionText = mentionText.substring(1);
      }
      
      // Find @ symbol before mention in text
      const mentionIndexInText = text.indexOf(mentionText);
      if (mentionIndexInText !== -1) {
        // Check if there's @ before the mention
        const beforeMention = text.substring(Math.max(0, mentionIndexInText - 1), mentionIndexInText);
        const startOffset = beforeMention === "@" ? mentionIndexInText - 1 : mentionIndexInText;
        const endOffset = beforeMention === "@" ? mentionIndexInText + mentionText.length : mentionIndexInText + mentionText.length;
        
        mentionsList.push({
          text: mentionText,
          mentionId,
          start: startOffset,
          end: endOffset,
        });
      }
    });

    // Extract inline styles (bold, italic, underline)
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      null
    );
    let textOffset = 0;
    let node;
    
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent.length;
        const parent = node.parentElement;
        
        // Check for bold
        if (parent && (parent.tagName === "B" || parent.tagName === "STRONG" || 
            parent.style.fontWeight === "bold" || parent.style.fontWeight === "700")) {
          inlineStyleRanges.push({
            offset: textOffset,
            length: textLength,
            style: "BOLD",
          });
        }
        
        // Check for italic
        if (parent && (parent.tagName === "I" || parent.tagName === "EM" || 
            parent.style.fontStyle === "italic")) {
          inlineStyleRanges.push({
            offset: textOffset,
            length: textLength,
            style: "ITALIC",
          });
        }
        
        // Check for underline
        if (parent && (parent.tagName === "U" || parent.style.textDecoration === "underline")) {
          inlineStyleRanges.push({
            offset: textOffset,
            length: textLength,
            style: "UNDERLINE",
          });
        }
        
        textOffset += textLength;
      }
    }

    // Create raw state format (matching draft-js format)
    const blocks = [
      {
        key: "simple",
        text: text,
        type: "unstyled",
        depth: 0,
        inlineStyleRanges: inlineStyleRanges,
        entityRanges: mentionsList.map((mention, index) => ({
          offset: mention.start,
          length: mention.end - mention.start,
          key: index,
        })),
        data: {},
      },
    ];

    const entityMap = {};
    mentionsList.forEach((mention, index) => {
      const user = mentions.find(
        (m) => m.OwnerUserName === mention.mentionId
      );
      entityMap[index] = {
        type: "MENTION",
        mutability: "IMMUTABLE",
        data: {
          mention: user || { name: mention.text, OwnerUserName: mention.mentionId },
        },
      };
    });

    return {
      blocks,
      entityMap,
    };
  }, [mentions]);

  const toHtml = useCallback(() => {
    if (!editorRefInternal.current) return "";
    const content = editorRefInternal.current.innerHTML;
    
    // Process mentions - ensure no @ symbol
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const mentionElements = tempDiv.querySelectorAll(".mention_class");
    
    mentionElements.forEach((el) => {
      // Remove @ symbol if present
      if (el.textContent.startsWith("@")) {
        el.textContent = el.textContent.substring(1);
      }
    });
    
    return tempDiv.innerHTML;
  }, []);

  const setValue = useCallback((value) => {
    if (typeof value === "string") {
      setContent(value);
      if (editorRefInternal.current) {
        editorRefInternal.current.innerHTML = value;
      }
    } else if (value && typeof value === "object") {
      // Handle raw state format if needed
      const html = convertRawToHTML(value);
      setContent(html);
      if (editorRefInternal.current) {
        editorRefInternal.current.innerHTML = html;
      }
    }
  }, [convertRawToHTML]);

  const reset = useCallback(() => {
    setContent("");
    if (editorRefInternal.current) {
      editorRefInternal.current.innerHTML = "";
    }
    notifyChange("");
  }, []);

  const notifyChange = useCallback((content) => {
    const rawState = getRawState();
    if (getValue) {
      getValue(rawState);
    }
    setContent(content);
    
    // Also call onChange if provided (for compatibility)
    if (onChange) {
      onChange(rawState);
    }
  }, [getRawState, getValue, onChange]);

  // Expose methods via ref (matching PostEditor pattern)
  useEffect(() => {
    if (editorRef) {
      editorRef.current = {
        setValue,
        reset,
        toHtml,
        getRawState,
      };
    }
  }, [editorRef, setValue, reset, toHtml, getRawState]);

  // Initialize with initialValue
  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue, setValue]);

  const scrollToSelected = useCallback(() => {
    if (suggestionsRef.current) {
      const selectedElement = suggestionsRef.current.querySelector(
        `.simple-post-editor__suggestion.selected`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, []);

  const checkForMention = useCallback((e) => {
    const selection = window.getSelection();
    if (!selection.rangeCount || !editorRefInternal.current) return;

    const range = selection.getRangeAt(0);
    
    // Get the plain text content - use a more reliable method
    // Create a range from start to cursor to get text before cursor
    const rangeBeforeCursor = range.cloneRange();
    rangeBeforeCursor.setStart(editorRefInternal.current, 0);
    rangeBeforeCursor.setEnd(range.endContainer, range.endOffset);
    const textBeforeCursor = rangeBeforeCursor.toString();
    
    // Also get all text for full context
    const allText = editorRefInternal.current.innerText || editorRefInternal.current.textContent || "";
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      // Extract text after @ symbol
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Check if there's a space or newline after @ (mention ended)
      if (textAfterAt.match(/^[\s\n]/)) {
        setShowSuggestions(false);
        return;
      }

      // Check if we're inside an existing mention
      const rangeBeforeCursorCheck = range.cloneRange();
      rangeBeforeCursorCheck.setStart(editorRefInternal.current, 0);
      rangeBeforeCursorCheck.setEnd(range.endContainer, range.endOffset);
      const htmlBeforeCursor = rangeBeforeCursorCheck.cloneContents();
      const tempDiv = document.createElement("div");
      tempDiv.appendChild(htmlBeforeCursor);
      const mentionElements = tempDiv.querySelectorAll(".mention_class");
      if (mentionElements.length > 0) {
        // Check if cursor is inside the last mention
        const lastMention = mentionElements[mentionElements.length - 1];
        const mentionText = lastMention.textContent;
        if (textAfterAt.startsWith(mentionText)) {
          setShowSuggestions(false);
          return;
        }
      }

      // Filter suggestions based on search text (search works as user types)
      const searchValueLower = textAfterAt.toLowerCase().trim();
      
      // Filter mentions: if search is empty, show all; otherwise filter by search text
      const filtered = mentions.filter((mention) => {
        if (!searchValueLower) {
          return true; // Show all mentions when just @ is typed
        }
        const mentionName = (mention.name || "").toLowerCase();
        return mentionName.includes(searchValueLower);
      });

      // Get cursor position for dropdown positioning
      const rect = range.getBoundingClientRect();
      const editorRect = editorRefInternal.current.getBoundingClientRect();
      
      // Position dropdown below cursor
      const cursorTopPos = rect.top - editorRect.top + rect.height + 2;
      const cursorLeftPos = Math.max(0, rect.left - editorRect.left);
      
      // Reset selectedIndex to 0 when search text changes
      setSearchValue((prevSearchValue) => {
        const shouldResetIndex = prevSearchValue !== textAfterAt;
        setSelectedIndex((prevIndex) => 
          shouldResetIndex ? 0 : Math.min(prevIndex, filtered.length - 1)
        );
        return textAfterAt;
      });
      
      // Always show suggestions if we're in mention mode (even if filtered is empty)
      setShowSuggestions(true);
      setSuggestions(filtered);
      setMentionStartPos(lastAtIndex);
      setCursorTop(cursorTopPos);
      setCursorLeft(cursorLeftPos);
    } else {
      setShowSuggestions(false);
    }
  }, [mentions]);

  const insertMention = useCallback((mention) => {
    if (!editorRefInternal.current || !mention) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    
    // Get text before cursor to find @ position
    const rangeBeforeCursor = range.cloneRange();
    rangeBeforeCursor.setStart(editorRefInternal.current, 0);
    rangeBeforeCursor.setEnd(range.endContainer, range.endOffset);
    const textBeforeCursor = rangeBeforeCursor.toString();
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex === -1) return;

    // Create range from @ to cursor to delete
    const deleteRange = range.cloneRange();
    deleteRange.setStart(editorRefInternal.current, 0);
    deleteRange.setEnd(range.endContainer, range.endOffset);
    
    // Find @ position in DOM
    const walker = document.createTreeWalker(
      editorRefInternal.current,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let charCount = 0;
    let atNode = null;
    let atOffset = 0;
    let node;
    
    while ((node = walker.nextNode())) {
      const nodeText = node.textContent;
      const nodeStart = charCount;
      const nodeEnd = charCount + nodeText.length;
      
      if (lastAtIndex >= nodeStart && lastAtIndex < nodeEnd) {
        atNode = node;
        atOffset = lastAtIndex - nodeStart;
        break;
      }
      charCount = nodeEnd;
    }

    if (!atNode) return;

    // Create range to delete from @ to cursor
    const deleteFromAtRange = document.createRange();
    deleteFromAtRange.setStart(atNode, atOffset);
    deleteFromAtRange.setEnd(range.endContainer, range.endOffset);
    
    // Delete the @ and search text
    deleteFromAtRange.deleteContents();

    // Create mention element (match PostEditor structure)
    const mentionSpan = document.createElement("span");
    mentionSpan.className = "mention_class position-relative";
    mentionSpan.setAttribute("data-mention-id", mention.OwnerUserName);
    mentionSpan.setAttribute("data-role-id", mention.name);
    mentionSpan.setAttribute("contenteditable", "false");
    mentionSpan.textContent = mention.name; // No @ symbol

    // Insert mention first
    deleteFromAtRange.insertNode(mentionSpan);

    // Position cursor right after mention and insert space using execCommand (most reliable)
    const afterMentionRange = document.createRange();
    afterMentionRange.setStartAfter(mentionSpan);
    afterMentionRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(afterMentionRange);
    
    // Insert space using execCommand - this is more reliable than DOM manipulation
    document.execCommand("insertText", false, " ");
    
    // Cursor is automatically positioned after the inserted space

    setShowSuggestions(false);
    notifyChange(editorRefInternal.current.innerHTML);
  }, [notifyChange]);

  const handleInput = useCallback((e) => {
    const contentValue = e.target.innerHTML;
    notifyChange(contentValue);
    // Check for mentions immediately - search works as user types
    checkForMention(e);
  }, [notifyChange, checkForMention]);

  const handleKeyDown = useCallback((e) => {
    // Handle mention suggestions navigation first
    if (showSuggestions) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prevIndex) => {
          const newIndex = prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0;
          setTimeout(() => scrollToSelected(), 0);
          return newIndex;
        });
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1;
          setTimeout(() => scrollToSelected(), 0);
          return newIndex;
        });
        return;
      }

      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        if (suggestions[selectedIndex]) {
          insertMention(suggestions[selectedIndex]);
        }
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setShowSuggestions(false);
        return;
      }

      // For regular typing keys, check for mentions after a short delay
      // This allows the input to be processed first
      if (e.key.length === 1 || e.key === "Backspace" || e.key === "Delete") {
        setTimeout(() => {
          checkForMention(e);
        }, 0);
      }
      
      // Don't prevent default for other keys when suggestions are open
      // This allows typing to continue filtering
      return;
    }
    
    // Check for mentions when typing @ or regular characters
    if (e.key === "@" || (e.key.length === 1 && !e.ctrlKey && !e.metaKey)) {
      setTimeout(() => {
        checkForMention(e);
      }, 0);
    }

    // Handle rich text formatting (matching PostEditor's RichUtils.handleKeyCommand)
    // Ctrl/Cmd + B for bold
    if ((e.ctrlKey || e.metaKey) && e.key === "b") {
      e.preventDefault();
      document.execCommand("bold", false, null);
      notifyChange(editorRefInternal.current.innerHTML);
      return;
    }

    // Ctrl/Cmd + I for italic
    if ((e.ctrlKey || e.metaKey) && e.key === "i") {
      e.preventDefault();
      document.execCommand("italic", false, null);
      notifyChange(editorRefInternal.current.innerHTML);
      return;
    }

    // Ctrl/Cmd + U for underline
    if ((e.ctrlKey || e.metaKey) && e.key === "u") {
      e.preventDefault();
      document.execCommand("underline", false, null);
      notifyChange(editorRefInternal.current.innerHTML);
      return;
    }
  }, [showSuggestions, suggestions, selectedIndex, insertMention, scrollToSelected, checkForMention, notifyChange]);

  const handleSuggestionClick = useCallback((mention, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    insertMention(mention);
  }, [insertMention]);

  // Update if mentions change
  useEffect(() => {
    setSuggestions(mentions);
  }, [mentions]);
  
  // Scroll to selected when selection changes
  useEffect(() => {
    if (showSuggestions) {
      setTimeout(() => scrollToSelected(), 0);
    }
  }, [selectedIndex, showSuggestions, scrollToSelected]);
  
  // Scroll to first item when suggestions appear
  useEffect(() => {
    if (showSuggestions) {
      setTimeout(() => {
        if (suggestionsRef.current) {
          const firstElement = suggestionsRef.current.querySelector(
            `.simple-post-editor__suggestion:first-child`
          );
          if (firstElement) {
            firstElement.scrollIntoView({ block: "nearest" });
          }
        }
      }, 0);
    }
  }, [showSuggestions]);

  // Use 'editor' class for compatibility with PostEditor styles
  const editorClassName = className === "PostEditor__input" 
    ? `editor ${className}` 
    : `simple-post-editor ${className || ""}`;

  return (
    <div className={editorClassName} style={style}>
      <div
        ref={editorRefInternal}
        className={className === "PostEditor__input" 
          ? "simple-post-editor__input" 
          : "simple-post-editor__input"}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder || "Type @ to mention someone..."}
        suppressContentEditableWarning
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="simple-post-editor__suggestions"
          style={{
            top: `${cursorTop}px`,
            left: `${cursorLeft}px`,
          }}
        >
          {suggestions.map((mention, index) => (
            <div
              key={mention.OwnerUserName || index}
              className={`simple-post-editor__suggestion ${
                index === selectedIndex ? "selected" : ""
              }`}
              onClick={(e) => handleSuggestionClick(mention, e)}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent losing focus from editor
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <EntryComponent mention={mention} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SimplePostEditor.propTypes = {
  /**
   * mentions {array} - array of names for `@`mentions to work
   */
  mentions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      OwnerUserName: PropTypes.string,
      OwnerLogo: PropTypes.string,
      OwnerShowInitals: PropTypes.bool,
      OwnerInitials: PropTypes.string,
    })
  ),
  /**
   * className {string} - className applied to top most Wrapper
   */
  className: PropTypes.string,
  /**
   * style {object} - inline style to be applied to top most Wrapper
   */
  style: PropTypes.object,
  /**
   * placeholder {string} - placeholder to display when editor has no text
   */
  placeholder: PropTypes.string,
  /**
   * getValue {function} - callback function that receives the editor value (raw state)
   */
  getValue: PropTypes.func,
  /**
   * onChange {function} - alternative callback for compatibility
   */
  onChange: PropTypes.func,
  /**
   * initialValue {string|object} - initial value for the editor (string HTML or raw state object)
   */
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * editorRef {object} - ref object to expose editor methods
   */
  editorRef: PropTypes.object,
};

SimplePostEditor.defaultProps = {
  mentions: [],
};

export default SimplePostEditor;

// Helper function to convert raw state to HTML (matches PostEditor's getHTMLfromRawState exactly)
export const getHTMLfromRawState = (rawData, mentionList = []) => {
  try {
    const parsed = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
    
    if (!parsed || !parsed.blocks || !Array.isArray(parsed.blocks)) {
      return "";
    }

    // Process each block
    let html = parsed.blocks
      .map((block) => {
        let text = block.text;
        
        if (block.entityRanges && block.entityRanges.length > 0) {
          // Sort entity ranges by offset (reverse order to avoid index issues)
          const sortedRanges = [...block.entityRanges].sort(
            (a, b) => b.offset - a.offset
          );

          sortedRanges.forEach((entityRange) => {
            const entity = parsed.entityMap[entityRange.key];
            if (entity && (entity.type === "MENTION" || entity.type === "mention")) {
              const mention = entity.data.mention;
              const start = entityRange.offset;
              const end = start + entityRange.length;
              const before = text.substring(0, start);
              let mentionText = text.substring(start, end);
              // Remove @ symbol if present
              if (mentionText.startsWith("@")) {
                mentionText = mentionText.substring(1);
              }
              const after = text.substring(end);

              text = `${before}<span class="mention_class position-relative" data-mention-id="${mention.OwnerUserName}">${mentionText}</span>${after}`;
            }
          });
        }

        return text;
      })
      .join("<br>");

    // Process mentions similar to PostEditor
    const doc = new DOMParser().parseFromString(html, "text/html");
    const commentElements = doc.body.getElementsByClassName("mention_class");
    const mentionsLength = commentElements.length;

    if (mentionsLength > 0) {
      for (let mention = 0; mention < mentionsLength; mention++) {
        const userId = doc
          .getElementsByClassName("mention_class")
          [mention].getAttribute("data-mention-id");

        const user = mentionList.find((item) => item.OwnerUserName === userId);

        // Mentions don't have @ symbol - just ensure it's not there
        const mentionElement = doc.getElementsByClassName("mention_class")[mention];
        if (mentionElement.innerHTML.startsWith("@")) {
          mentionElement.innerHTML = mentionElement.innerHTML.substring(1);
        }
      }
      return doc.body.innerHTML;
    } else {
      return doc.body.innerHTML;
    }
  } catch (err) {
    return "";
  }
};
